import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Eye, EyeOff, Mail, Lock, User, AtSign, Music, Users, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    username: '',
    role: 'fan',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password || !formData.full_name || !formData.username) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (formData.username.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }

    setLoading(true);
    try {
      const userData = await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        username: formData.username.toLowerCase(),
        role: formData.role,
      });
      toast.success(`Welcome to M4manna, ${userData.full_name}!`);
      navigate('/dashboard');
    } catch (error) {
      toast.error('Registration Failed', {
        description: error.response?.data?.detail || 'Failed to create account.',
      });
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { value: 'fan', label: 'Fan', icon: Users, description: 'Stream, download, and support artists' },
    { value: 'artist', label: 'Artist', icon: Music, description: 'Upload music and build your audience' },
    { value: 'mentor', label: 'Mentor', icon: GraduationCap, description: 'Guide artists and offer sessions' },
  ];

  return (
    <Layout>
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-2">Join M4manna</h1>
            <p className="text-[#A0A0A0]">Create your account and start your worship journey</p>
          </div>

          <div className="bg-[#16181D] border border-[#1E2028] rounded-3xl p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-[#A0A0A0] mb-3">I want to join as</label>
                <div className="grid grid-cols-3 gap-2">
                  {roles.map((role) => {
                    const Icon = role.icon;
                    const selected = formData.role === role.value;
                    return (
                      <button
                        key={role.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, role: role.value })}
                        className={`flex flex-col items-center p-3 rounded-2xl border-2 transition-all ${
                          selected
                            ? 'border-[#FFE500] bg-[#FFE500]/10'
                            : 'border-[#1E2028] bg-[#0A0B0F] hover:border-[#FFE500]/30'
                        }`}
                        data-testid={`role-${role.value}`}
                      >
                        <Icon className={`h-5 w-5 mb-1.5 ${selected ? 'text-[#FFE500]' : 'text-[#A0A0A0]'}`} />
                        <span className={`text-sm font-semibold ${selected ? 'text-white' : 'text-[#A0A0A0]'}`}>
                          {role.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-[#666] text-center mt-2">
                  {roles.find((r) => r.value === formData.role)?.description}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="full_name" className="block text-sm font-semibold text-[#A0A0A0] mb-2">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666] pointer-events-none" />
                    <input
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="input-modern pl-11"
                      data-testid="register-fullname"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="username" className="block text-sm font-semibold text-[#A0A0A0] mb-2">Username</label>
                  <div className="relative">
                    <AtSign className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666] pointer-events-none" />
                    <input
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      placeholder="johndoe"
                      className="input-modern pl-11"
                      data-testid="register-username"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-[#A0A0A0] mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666] pointer-events-none" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="input-modern pl-11"
                    data-testid="register-email"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="password" className="block text-sm font-semibold text-[#A0A0A0] mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666] pointer-events-none" />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Min 6 chars"
                      className="input-modern pl-11"
                      data-testid="register-password"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-semibold text-[#A0A0A0] mb-2">Confirm</label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#666] pointer-events-none" />
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm"
                      className="input-modern pl-11 pr-11"
                      data-testid="register-confirm-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#666] hover:text-white"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <label className="flex items-start gap-2 text-sm text-[#A0A0A0]">
                <input type="checkbox" id="terms" required className="mt-1 rounded border-[#1E2028] bg-[#0A0B0F]" />
                <span>
                  I agree to the{' '}
                  <Link to="/terms" className="text-[#FFE500] hover:underline">Terms of Service</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-[#FFE500] hover:underline">Privacy Policy</Link>
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-full bg-[#FFE500] text-black font-bold hover:bg-[#F0D800] disabled:opacity-60 transition-all"
                data-testid="register-submit"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>

            <div className="mt-6 text-center text-[#A0A0A0] text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-[#FFE500] hover:underline font-semibold">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Register;
